import { put, take, call, fork, all, select } from 'redux-saga/effects';
import { isEmpty } from 'lodash';
import {
  makeDataChartUpdateRequest,
  setDataChartsData,
  setDataChartsError,
  resetDomain,
  resetChartsData,
} from '../../../reducers/dataChartSlice';
import {
  DATA_MIDDLE_CHART_ID,
  DATA_TOP_CHART_ID,
  GOOGLE_ANALYTICS_DATA_EXPLORER_APPLY,
} from '../../../common/constants';
import {
  getChartTypeByChartName,
  getChartUnitByChartName,
} from '../../../common/data/new_charts_page_metadata';
import { setGoogleAnalyticTracking } from '../../../common/redux/googleAnalyticTracking/actions';
import {
  getAstronautFilteredData,
  getAstronautLinkedFilteredData,
  getAstronautSelectedProperties,
  setAstronautLatestAppliedProperties,
  setAstronautSelectedProperties,
} from '../../../reducers/astronautPropertySelectSlice';
import {
  initialDataExplorerLoadManager,
  deleteChartByKey,
} from '../../../reducers/dataExplorerLoadManagerSlice';
import {
  compareArraysValues,
  getConfigKeyForEntity,
  getSearchParams,
  updateSearchParams,
} from '../../../common/utils';
import { getSelectedDisabledFields } from '../../AstronautDataExplorer/AstronautFilterMenu/utils';
import { dataConfigChartSelectors } from '../../../reducers/dataConfigChartSlice';
import withLoading from '../../../sagas/loadingSaga';
import { DATE_RANGE_HOUR, DATE_RANGE_DAY } from '../RangesLogic';

export default function* dataChartSaga(api) {
  while (true) {
    const updateDataChartRequestedAction = yield take(
      makeDataChartUpdateRequest.type,
    );
    yield* withLoading(function* () {
      try {
        yield put(resetChartsData());
        const { entityId, startDate, endDate, linkedEntityId, navigate } =
          updateDataChartRequestedAction.payload;
        const dataChartConfig = yield select(
          dataConfigChartSelectors.selectConfigChartsData,
        );

        const currentRobot = yield select(
          (state) => state.entityInfo?.entityInfo,
        );
        const entityType = currentRobot?.type;
        const configKey = getConfigKeyForEntity(entityType);

        if (!entityType) {
          yield put(setDataChartsError('Unable to determine entity type.'));
          return;
        }

        const dataKeysDictionary = yield select((state) =>
          getAstronautSelectedProperties(state, entityType),
        );

        // Calculate range from startDate and endDate
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffInHours = (end - start) / (1000 * 60 * 60);

        let currentDataRange;
        if (diffInHours <= 1) {
          currentDataRange = DATE_RANGE_HOUR;
        } else {
          currentDataRange = DATE_RANGE_DAY;
        }

        const filteredData = yield select(getAstronautFilteredData);
        const linkedFilteredData = yield select(getAstronautLinkedFilteredData);

        const currentRobotName = currentRobot?.name?.replace('astronaut-', '');

        const disabledValues = getSelectedDisabledFields(
          filteredData || [],
          dataKeysDictionary,
          'dataLocationInStore',
        );

        const disabledValuesLinked = getSelectedDisabledFields(
          linkedFilteredData?.configurations || [],
          dataKeysDictionary,
          'dataLocationInStore',
          linkedFilteredData?.linkedName,
        );

        const topChartDataKeys = [];
        const middleChartDataKeys = [];
        if (!isEmpty(dataKeysDictionary)) {
          Object.keys(dataKeysDictionary).forEach((currentDataKey) => {
            // test to see if currentDataKey is with linked value of the current robot
            if (currentDataKey.includes(currentRobotName)) {
              return;
            }

            const regex = /\s\(\d+\)/g;
            const removedRemovedRobotName = currentDataKey.replace(regex, '');

            if (
              currentDataRange === DATE_RANGE_DAY &&
              (disabledValues?.includes(removedRemovedRobotName) ||
                disabledValuesLinked?.includes(removedRemovedRobotName))
            ) {
              // when the user is coming from an empty URL and he has disabled day fields in his local storage from previous sessions
              return;
            }

            if (dataKeysDictionary[currentDataKey] === DATA_TOP_CHART_ID) {
              topChartDataKeys.push(currentDataKey);
            } else if (
              dataKeysDictionary[currentDataKey] === DATA_MIDDLE_CHART_ID
            ) {
              middleChartDataKeys.push(currentDataKey);
            }
          });
        }

        const updatedDataKeysDictionary = {
          ...topChartDataKeys.reduce(
            (prev, next) => ({ ...prev, [next]: '1' }),
            {},
          ),
          ...middleChartDataKeys.reduce(
            (prev, next) => ({ ...prev, [next]: '2' }),
            {},
          ),
        };

        yield put(
          setAstronautLatestAppliedProperties({
            entityType,
            properties: updatedDataKeysDictionary,
          }),
        );
        yield put(
          setAstronautSelectedProperties({
            entityType,
            properties: updatedDataKeysDictionary,
          }),
        );
        // update active graphs
        if (configKey) {
          localStorage.updateObjectProperty(
            configKey,
            'activeGraphs',
            updatedDataKeysDictionary,
          );
        }

        const chartsData = [];

        chartsData.push({
          chartId: DATA_TOP_CHART_ID,
          dataKeys: topChartDataKeys,
        });

        chartsData.push({
          chartId: DATA_MIDDLE_CHART_ID,
          dataKeys: middleChartDataKeys,
        });

        /**
         * Clean up URL parameters to match actual available data.
         *
         * This logic is needed because:
         * 1. Users can manually add signals to the URL that don't exist in the backend
         * 2. When the backend returns data, it only includes signals that actually have data
         * 3. We need to remove invalid signals from the URL to keep it consistent with reality
         *
         * Example:
         * - URL has: ?1=ValidSignal,InvalidSignal&2=AnotherSignal
         * - Backend returns data only for: ValidSignal, AnotherSignal
         * - This code updates URL to: ?1=ValidSignal&2=AnotherSignal
         * - InvalidSignal is removed because it has no data
         *
         * This prevents users from seeing empty charts and ensures URL reflects actual data availability.
         */
        chartsData.forEach((currentChart) => {
          const currentParamsForChartId = getSearchParams(currentChart.chartId);
          if (
            !currentParamsForChartId ||
            (currentParamsForChartId &&
              !compareArraysValues(
                currentParamsForChartId.split(','),
                currentChart.dataKeys,
              ))
          ) {
            updateSearchParams(
              currentChart.chartId,
              currentChart.dataKeys,
              true,
              navigate,
            );
          }
        });

        yield put(
          initialDataExplorerLoadManager({
            chartsData: chartsData.filter(
              (chart) => chart.dataKeys.length !== 0,
            ),
            dataChartConfig,
          }),
        );
        yield put(resetDomain());
        const selectedChartTopics = JSON.stringify(updatedDataKeysDictionary)
          .replace(/{/gi, '')
          .replace(/}/gi, '');
        yield put(
          setGoogleAnalyticTracking(
            GOOGLE_ANALYTICS_DATA_EXPLORER_APPLY,
            selectedChartTopics,
            `${startDate.toString()}-${endDate.toString()}`,
          ),
        );

        yield all(
          chartsData.map((currentChart) => {
            if (currentChart.dataKeys.length === 0) {
              return;
            }

            return fork(
              getChartData,
              api,
              entityId,
              linkedFilteredData?.linkedName,
              startDate,
              endDate,
              currentChart.dataKeys,
              linkedEntityId,
              currentChart.chartId,
              dataChartConfig,
            );
          }),
        );
      } catch (error) {
        yield put(setDataChartsError(error));
      }
    });
  }
}

/** Create requests to get chart data for a specific chart
 * @param {string} entityId - entity id
 * @param {Date} startDate - Start date of the data range
 * @param {Date} endDate - Start date of the data range
 * @param {Array} dataKeys - Array of data keys of interest to the end user/that they want to see visualized in a chart
 * @param {String} linkedEntityId - Id of the selected linked element
 * @param {string} chartId - Chart id
 * @param {Object} dataChartConfig - Object that provides answer to the question "In what kind of chart (line/status/spline) is this metric displayed?"
 */
function* getChartData(
  api,
  entityId,
  linkedName,
  startDate,
  endDate,
  dataKeys,
  linkedEntityId,
  chartId,
  dataChartConfig,
) {
  function getModifiedDataFromBackend(backendData) {
    backendData.forEach((currentChartValue, currentChartIndex) => {
      backendData[currentChartIndex].type = getChartTypeByChartName(
        currentChartValue.name,
        dataChartConfig,
      );
      backendData[currentChartIndex].unit = getChartUnitByChartName(
        currentChartValue.name,
        dataChartConfig,
      );
    });
    return backendData;
  }

  try {
    const chartData = yield call(
      api.getChartDetails,
      entityId,
      startDate,
      endDate,
      dataKeys,
      linkedEntityId,
    );
    const filteredChartData = chartData.data.filter((el) => {
      return el != null;
    });

    getModifiedDataFromBackend(filteredChartData);

    filteredChartData.forEach((data) => {
      const linkedGraph = data.graphs.find(
        // find the graph that belongs to the linked robot
        (graph) => graph.entityId === linkedEntityId,
      );

      if (linkedGraph) {
        // Add the linked robot number to the graph
        linkedGraph.name = `${linkedGraph.name} (${linkedName})`;
      }
    });

    const missingKeys = dataKeys.filter(
      (key) => !filteredChartData.some((item) => item.name === key),
    );

    yield put(deleteChartByKey(missingKeys));
    yield put(
      setDataChartsData({
        chartData: filteredChartData,
        startDate,
        endDate,
        chartId,
      }),
    );
  } catch (error) {
    yield put(setDataChartsError(error));
  }
}
